import React, { createContext, useState, useContext, ReactNode } from 'react';

type GradientType = 'default' | 'success' | 'warning' | 'danger' | 'custom';

interface GradientContextType {
    gradientColors: {
        from: string;
        via?: string;
        to: string;
    };
    gradientType: GradientType;
    gradientOpacity: number;
    setGradient: (type: GradientType, opacity?: number, customColors?: { from: string; via?: string; to: string }) => void;
}

const defaultGradient = {
    from: 'accent/30',
    to: 'accent/5',
};

const gradientPresets = {
    default: defaultGradient,
    success: {
        from: 'emerald-500/30',
        to: 'teal-400/5',
    },
    warning: {
        from: 'amber-500/30',
        to: 'yellow-400/5',
    },
    danger: {
        from: 'rose-500/30',
        to: 'red-400/5',
    },
    custom: defaultGradient,
};

export const GradientContext = createContext<GradientContextType>({
    gradientColors: defaultGradient,
    gradientType: 'default',
    gradientOpacity: 1,
    setGradient: () => { },
});

export const useGradient = () => useContext(GradientContext);

interface GradientProviderProps {
    children: ReactNode;
}

export const GradientProvider: React.FC<GradientProviderProps> = ({ children }) => {
    const [gradientColors, setGradientColors] = useState(defaultGradient);
    const [gradientType, setGradientType] = useState<GradientType>('default');
    const [gradientOpacity, setGradientOpacity] = useState(1);

    const setGradient = (
        type: GradientType,
        opacity = 1,
        customColors?: { from: string; via?: string; to: string }
    ) => {
        setGradientType(type);
        setGradientOpacity(opacity);

        if (type === 'custom' && customColors) {
            setGradientColors(customColors);
        } else {
            setGradientColors(gradientPresets[type]);
        }
    };

    return (
        <GradientContext.Provider
            value={{
                gradientColors,
                gradientType,
                gradientOpacity,
                setGradient,
            }}
        >
            {children}
        </GradientContext.Provider>
    );
}; 